import { useEffect, useRef, useState, type ReactNode } from "react";
import { buttonVariants } from "./button";

const MOBILE_PDF_QUERY = "(max-width: 1023px), (hover: none) and (pointer: coarse)";

export function isPdf(mime: string) {
  return mime.split(";")[0]?.trim().toLowerCase() === "application/pdf";
}

function MobilePdfViewer({ url, filename, title, className = "", fallbackAction }: { url: string; filename: string; title: string; className?: string; fallbackAction?: ReactNode }) {
  const pages = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<{ url: string; status: "loading" | "ready" | "error" }>({ url, status: "loading" });
  useEffect(() => {
    const container = pages.current!;
    let width = 0;
    let controller: AbortController | undefined;
    const resize = () => {
      const nextWidth = container.clientWidth;
      if (!nextWidth || nextWidth === width) return;
      width = nextWidth;
      controller?.abort();
      const current = new AbortController();
      controller = current;
      container.replaceChildren();
      setResult({ url, status: "loading" });
      // Keep the parser, worker and their dependencies out of the main bundle.
      void import("./pdf-document-renderer").then(({ renderPdfDocument }) => {
        current.signal.throwIfAborted();
        return renderPdfDocument(url, container, nextWidth, current.signal);
      }).then(() => {
        if (!current.signal.aborted) setResult({ url, status: "ready" });
      }).catch(() => {
        if (!current.signal.aborted) setResult({ url, status: "error" });
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => { observer.disconnect(); controller?.abort(); };
  }, [url]);
  const status = result.url === url ? result.status : "loading";
  return <div className={`avm-document-viewer__pdf ${className}`} role="region" aria-label={title} aria-busy={status === "loading"}>
    {status === "loading" && <p role="status">Učitavanje dokumenta…</p>}
    {status === "error" && <div role="alert">
      <p>Dokument se ne može prikazati. Preuzmite ga i otvorite na uređaju.</p>
      {fallbackAction ?? <a className={buttonVariants({ variant: "secondary", size: "sm" })} href={url} download={filename}>Preuzmi</a>}
    </div>}
    <div ref={pages} className="avm-document-viewer__pages" style={status === "error" ? { visibility: "hidden", height: 0, overflow: "hidden" } : undefined} />
  </div>;
}

/** Uses the native desktop viewer and lazily renders PDFs on mobile/touch devices. */
export function ResponsivePdfViewer({ url, title, filename = title, iframeSrc = url, iframeClassName, mobileClassName, fallbackAction }: {
  url: string; title: string; filename?: string; iframeSrc?: string; iframeClassName?: string; mobileClassName?: string; fallbackAction?: ReactNode;
}) {
  const [mobile, setMobile] = useState(() => window.matchMedia?.(MOBILE_PDF_QUERY).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_PDF_QUERY);
    if (!media) return;
    const changed = () => setMobile(media.matches);
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, []);
  return mobile
    ? <MobilePdfViewer key={url} url={url} title={title} filename={filename} className={mobileClassName} fallbackAction={fallbackAction} />
    : <iframe src={iframeSrc} title={title} className={iframeClassName} />;
}
